import Container from "@bmi-digital/components/container";
import Grid from "@bmi-digital/components/grid";
import Section from "@bmi-digital/components/section";
import Tabs from "@bmi-digital/components/tabs";
import Tab from "@mui/material/Tab";
import classnames from "classnames";
import { graphql } from "gatsby";
import React from "react";
import TeamList from "../components/TeamList";
import { useConfig } from "../contexts/ConfigProvider";
import withGTM from "../utils/google-tag-manager";
import RichText from "./RichText";
import {
  classes,
  StyledSection,
  TeamCategoryContainer
} from "./styles/TeamSectionStyles";
import type { RichTextData } from "./RichText";
import type { TeamMemberData } from "../components/TeamList";
import type { TabProps } from "@mui/material/Tab";
import type { PayloadTeamCategory, PayloadTeamSection } from "../types/payload";

export type TeamCategory = Pick<PayloadTeamCategory, "title"> & {
  description: RichTextData | null;
  teamMembers: readonly TeamMemberData[] | null;
};

export type TeamSectionData = Pick<
  PayloadTeamSection,
  "blockType" | "title" | "backgroundColor"
> & {
  teamCategories: readonly TeamCategory[];
};

type Props = {
  data: TeamSectionData;
  className?: string;
};

const GTMTab = withGTM<TabProps>(Tab, {
  label: "label"
});

const TeamSection = ({ data, className }: Props) => {
  const { renderTeamCategoriesAsRows } = useConfig();

  return (
    <StyledSection
      className={classnames(
        className,
        classes[data.backgroundColor?.toLowerCase() || "white"]
      )}
    >
      <Grid container spacing={3}>
        <Grid xs={12}>
          {data.title && (
            <Section.Title variant="h2" hasUnderline>
              {data.title}
            </Section.Title>
          )}
          {!renderTeamCategoriesAsRows ? (
            <Tabs
              component={Container}
              className={classes.tabs}
              tabComponent={(props: TabProps) => (
                <GTMTab
                  gtm={{ id: "selector-tabs2", action: "Selector - Tabs" }}
                  {...props}
                />
              )}
            >
              {data.teamCategories.map((category, index) => (
                <Tabs.TabPanel
                  className={classes.tabPanel}
                  key={index}
                  heading={category.title}
                  index={index}
                >
                  <Container>
                    <TeamCategorySection
                      title={category.title}
                      description={category.description}
                      teamMembers={category.teamMembers}
                    />
                  </Container>
                </Tabs.TabPanel>
              ))}
            </Tabs>
          ) : (
            data.teamCategories.map((category, index) => (
              <TeamCategorySection
                key={category.title}
                title={category.title}
                description={
                  index === 0 && category.description
                    ? category.description
                    : null
                }
                teamMembers={category.teamMembers}
              />
            ))
          )}
        </Grid>
      </Grid>
    </StyledSection>
  );
};

const TeamCategorySection = (props: Omit<TeamCategory, "id">) => (
  <TeamCategoryContainer>
    {props.description && (
      <RichText
        document={props.description}
        className={classes.description}
        hasNoBottomMargin
      />
    )}
    {props.teamMembers && <TeamList data={props.teamMembers} />}
  </TeamCategoryContainer>
);

export default TeamSection;

export const query = graphql`
  fragment TeamSectionFragment on PayloadTeamSection {
    blockType
    title
    teamCategories {
      title
      description {
        ...RichTextFragment
      }
      teamMembers {
        ...TeamMemberFragment
      }
    }
    backgroundColor
  }
`;
